import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Modal, Switch } from 'antd';
import PropTypes from 'prop-types';
import { FormOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { debounce } from 'lodash';
import { Box, Text, Flex, Button, Image } from 'atoms';
import { Rupee, Highlighted } from 'components/common';
import upiNotFound from 'assets/upi-not-found.svg';
import * as Styled from './UPIAutoComplete.style';
import { getUpiReferenceNumbers } from 'store/salesmanSlice';

const format = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/; // eslint-disable-line

const UPI_REF_LENGTH = 12;

export function AutoCompleteUpi({
	showUpiPopup,
	storeData,
	setShowUpiPopup,
	formRef,
	UPIRefFieldIndex,
}) {
	const dispatch = useDispatch();
	const [refernceNo, setReferenceNo] = useState([]);
	const [UPISearchVal, setUPISearchVal] = useState('');
	const [errorVal, setErrorVal] = useState('');
	const [loading, setloading] = useState(false);
	const [viewAll, setViewAll] = useState(false);
	const upiAmount = formRef?.current?.values?.upiData?.[UPIRefFieldIndex]?.amount;

	console.log(storeData, 'storeData');

	const getUpiData = useCallback(
		(queryParams, isviewAll) => {
			setloading(true);
			dispatch(getUpiReferenceNumbers(queryParams))
				.then((res) => {
					let filteredUPIData = [];

					if (isviewAll) {
						filteredUPIData = res?.filter((item) => item?.credit >= upiAmount);
					} else {
						filteredUPIData = res?.filter((item) => item?.credit === upiAmount);
					}
					setReferenceNo([...filteredUPIData]);
				})
				.finally(() => {
					setloading(false);
				});
		},
		[dispatch, upiAmount]
	);

	const debouncedSearch = useRef(
		debounce(async (query, isviewAll) => {
			const queryParams = {
				store_id: storeData?.store_id,
				salesman_id: storeData?.salesman_id,
				amount: upiAmount,
				query,
			};

			getUpiData(queryParams, isviewAll);
		}, 500)
	).current;

	useEffect(
		() => () => {
			debouncedSearch.cancel();
		},
		[debouncedSearch]
	);

	useEffect(() => {
		/**
		 * on upi modal open, if there is no ref number means, will make api call by default
		 */
		if (UPISearchVal === '') {
			const queryParams = {
				store_id: storeData?.store_id,
				salesman_id: storeData?.salesman_id,
				amount: upiAmount,
			};

			getUpiData(queryParams, viewAll);
		}
	}, [
		dispatch,
		storeData?.store_id,
		upiAmount,
		viewAll,
		UPISearchVal,
		getUpiData,
		storeData?.salesman_id,
	]);

	useEffect(() => {
		if (UPISearchVal?.length >= UPI_REF_LENGTH) {
			debouncedSearch(UPISearchVal, viewAll);
		}
	}, [debouncedSearch, UPISearchVal, viewAll]);

	const handleChange = (e) => {
		const { value } = e.target;

		if (value.length >= UPI_REF_LENGTH) {
			setErrorVal('');
		} else {
			setErrorVal(`Enter atleast ${UPI_REF_LENGTH} reference number`);
		}

		if (format.test(value)) {
			setErrorVal('Special characters not allowed');
		}

		setUPISearchVal(value);
	};

	const handleSelectUPI = (data, bank_statement_id = null) => {
		formRef?.current?.setFieldValue(`upiData[${UPIRefFieldIndex}].reference_number`, data);
		formRef?.current?.setFieldValue(
			`upiData[${UPIRefFieldIndex}].bank_statement_id`,
			bank_statement_id
		);
		setShowUpiPopup(false);
	};

	return (
		<Modal
			destroyOnClose
			visible={showUpiPopup}
			footer={null}
			width={320}
			onCancel={() => setShowUpiPopup(false)}
			className="upiModelPopup"
		>
			<Box>
				<Box marginBottom="1rem">
					<Styled.StoreName>{storeData?.store_name || 'NA'}</Styled.StoreName>
					<Styled.InvoiceNumber>{storeData?.invoice_number || ''}</Styled.InvoiceNumber>
				</Box>
				<Box marginBottom="1.25rem">
					<Styled.InputLabel>*UPI Ref no:</Styled.InputLabel>
					<Styled.InputField
						value={UPISearchVal}
						placeholder={`Enter last ${UPI_REF_LENGTH} digits`}
						onChange={handleChange}
						maxLength={18}
					/>
					{UPISearchVal !== '' && (
						<Styled.ClearIcon onClick={() => setUPISearchVal('')} />
					)}
					<Styled.InputErrorMsg as="p">{errorVal}</Styled.InputErrorMsg>
				</Box>
				<Flex justifyContent="space-between" marginBottom="1rem">
					<Box>
						<Switch
							checked={viewAll}
							size="small"
							style={{ marginRight: '8px' }}
							onClick={() => setViewAll(!viewAll)}
						/>
						<Box as="span" fontSize="10px" lineHeight="15px" color="#000000">
							View All
						</Box>
					</Box>
					<Button
						bg="transparent"
						p="0"
						border="0"
						cursor="pointer"
						disabled={
							refernceNo?.length > 0 ||
							UPISearchVal.length < UPI_REF_LENGTH ||
							format.test(UPISearchVal)
						}
						onClick={() => handleSelectUPI(UPISearchVal)}
					>
						<Flex alignItems="center">
							<FormOutlined
								className="hasDisabled"
								style={{ color: '#007FFF', marginRight: '8px' }}
							/>
							<Text fontSize="10px" lineHeight="15px" color="#007FFF">
								Add manually 2
							</Text>
						</Flex>
					</Button>
				</Flex>

				{refernceNo?.length > 0 && !loading && (
					<Box as="ul" height="200px" overflow="auto">
						{refernceNo?.map((data) => (
							<Styled.SearchList
								key={data?.bank_statement_id}
								as="li"
								onClick={() =>
									handleSelectUPI(
										data?.transaction_number,
										data?.bank_statement_id
									)
								}
								cursor="pointer"
								alignItems="center"
							>
								<Box fontSize="14px" lineHeight="15px" color="#000000">
									<Highlighted
										text={data?.transaction_number || 'NA'}
										highlight={UPISearchVal}
									/>
									<Text fontSize="8px">{data?.remitter_name || ''}</Text>
								</Box>
								<Rupee style={{ fontWeight: '300', fontSize: '10px' }}>
									{data?.credit || 0}
								</Rupee>
							</Styled.SearchList>
						))}
					</Box>
				)}

				{refernceNo?.length === 0 && !loading && (
					<Flex
						flexDirection="column"
						justifyContent="center"
						alignItems="center"
						minHeight="200px"
					>
						<Image src={upiNotFound} alt="upiNotFound" />
						<Text textAlign="center" fontSize="10px" color="#484848" mt=".5rem">
							Search Not Found
						</Text>
					</Flex>
				)}
				{loading && (
					<Text width="100%" textAlign="center" pt="4rem" minHeight="200px">
						Loading...
					</Text>
				)}
			</Box>
		</Modal>
	);
}

AutoCompleteUpi.propTypes = {
	showUpiPopup: PropTypes.bool,
	storeData: PropTypes.object,
	setShowUpiPopup: PropTypes.func,
	formRef: PropTypes.object,
	UPIRefFieldIndex: PropTypes.number,
};
